import interpolate from 'color-interpolate'
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import {
    deletePendingLayoutSubmission,
    ensureAnonymousSession,
    fetchApprovedCommunityLayouts,
    fetchCurrentUserVotes,
    fetchPendingLayouts,
    fetchPromotedCommunityLayouts,
    getAuthenticatedUserId,
    isCommunityEnabled,
    isCurrentUserAdmin as fetchIsCurrentUserAdmin,
    moderateLayoutSubmission,
    submitLayout,
    toggleLayoutVote,
    uploadLayoutPreview
} from './communityApi'
import {
    buildInitialConfig,
    CONFIG_COOKIE_DEBOUNCE_MS,
    CONFIG_COOKIE_MAX_AGE_SECONDS,
    CONFIG_COOKIE_NAME,
    toPersistedConfig
} from './configPersistence'
import Controls from './Controls'
import {
    Container,
    Frame,
    InstructionBody,
    InstructionButton,
    InstructionOverlay,
    InstructionPanel,
    Skewer
} from './Elements'
import {trackEvent} from './analytics'
import {gradients} from './gradients'
import Kaleidoscope from './Kaleidoscope'
import {layouts} from './layouts'
import {sequence} from './sequence'
import {
    advanceAll,
    advanceLayout,
    advanceSequence,
    advanceSingle,
    selectLayoutByLabel
} from './updateStrategies'
import {
    buildQuadrant,
    coinFlip,
    increment
} from './utils'

const communityLabelFor = submissionId => `community:${submissionId}`
const previewMaxSide = Math.max(64, Number(process.env.REACT_APP_LAYOUT_PREVIEW_MAX_SIDE || 800))
const previewWebpQuality = Math.min(1, Math.max(0.1, Number(process.env.REACT_APP_LAYOUT_PREVIEW_WEBP_QUALITY || 0.82)))
const submissionTitlePattern = /^[a-z0-9]+(-[a-z0-9]+)*$/
const rateLimitErrorMessage = 'Please wait 5 minutes before submitting another design.'

const toSubmitDesignErrorMessage = error => {
    const message = String(error?.message || '')
    const normalizedMessage = message.toLowerCase()
    const isLayoutSubmissionPolicyError = normalizedMessage.includes('row-level security')
        && normalizedMessage.includes('layout_submissions')

    if (isLayoutSubmissionPolicyError) {
        return rateLimitErrorMessage
    }

    return message || 'Unable to submit design.'
}

const canvasToBlob = (canvas, type, quality) => new Promise(resolve => {
    if (!canvas?.toBlob) {
        resolve(null)
        return
    }

    canvas.toBlob(resolve, type, quality)
})

const App = () => {
    const [config, setConfig] = useState(buildInitialConfig)
    const [promotedCommunityLayouts, setPromotedCommunityLayouts] = useState([])
    const [approvedCommunityLayouts, setApprovedCommunityLayouts] = useState([])
    const [communityVoteBySubmissionId, setCommunityVoteBySubmissionId] = useState({})
    const [isCommunityLoading, setIsCommunityLoading] = useState(false)
    const [isCommunitySubmitting, setIsCommunitySubmitting] = useState(false)
    const [isCommunityVoting, setIsCommunityVoting] = useState(false)
    const [isCommunityModerating, setIsCommunityModerating] = useState(false)
    const [communityError, setCommunityError] = useState(null)
    const [communityNotice, setCommunityNotice] = useState(null)
    const [isCommunityAuthReady, setIsCommunityAuthReady] = useState(!isCommunityEnabled())
    const [isCurrentUserAdmin, setIsCurrentUserAdmin] = useState(false)
    const [pendingCommunityLayouts, setPendingCommunityLayouts] = useState([])
    const [isPendingCommunityLoading, setIsPendingCommunityLoading] = useState(false)
    const [selectedApprovedCommunityDesignId, setSelectedApprovedCommunityDesignId] = useState('')
    const [communityReloadKey, setCommunityReloadKey] = useState(0)

    const configPersistTimeoutRef = useRef(null)
    const kaleidoscopeRef = useRef(null)

    const {
        dimensions,
        squareSize,
        interval,
        showInstructions,
        isUpdating,
        isSkewed,
        lockMode,
        isLayoutInverted,
        staggerMode,
        updateMode,
        duration,
        stagger,
        colorStops,
        isGradientInverted,
        values
    } = config

    const isCommunityConfigured = isCommunityEnabled()
    const isStaggered = staggerMode !== 'off'
    const isReversed = staggerMode === 'inward'
    const effectiveColorStops = useMemo(() => (
        isGradientInverted ? [...colorStops].reverse() : colorStops
    ), [colorStops, isGradientInverted])
    const colors = useMemo(() => interpolate(effectiveColorStops), [effectiveColorStops])
    const effectiveValues = useMemo(() => (
        isLayoutInverted
            ? values.map(value => (value + 2) % 4)
            : values
    ), [isLayoutInverted, values])
    const serializedLayoutValues = useMemo(() => JSON.stringify(values), [values])

    const builtInLayoutSet = useMemo(() => layouts[dimensions] || {}, [dimensions])
    const promotedCommunityLayoutsForDimension = useMemo(() => {
        return promotedCommunityLayouts.filter(layout => layout.dimensions === dimensions)
    }, [promotedCommunityLayouts, dimensions])
    const approvedCommunityLayoutsForDimension = useMemo(() => {
        return approvedCommunityLayouts.filter(layout => layout.dimensions === dimensions)
    }, [approvedCommunityLayouts, dimensions])
    const approvedCommunityLayoutsById = useMemo(() => {
        return approvedCommunityLayoutsForDimension.reduce((acc, layout) => {
            acc[layout.id] = layout
            return acc
        }, {})
    }, [approvedCommunityLayoutsForDimension])
    const mergedLayoutSet = useMemo(() => {
        const communitySet = promotedCommunityLayoutsForDimension.reduce((acc, layout) => {
            acc[communityLabelFor(layout.id)] = layout.layout_values
            return acc
        }, {})

        return {
            ...builtInLayoutSet,
            ...communitySet
        }
    }, [builtInLayoutSet, promotedCommunityLayoutsForDimension])
    const mergedLayouts = useMemo(() => ({
        ...layouts,
        [dimensions]: mergedLayoutSet
    }), [dimensions, mergedLayoutSet])

    const layoutOptions = useMemo(() => {
        const builtIns = Object.keys(builtInLayoutSet).map(label => ({
            value: label,
            label,
            isCommunity: false
        }))
        const community = promotedCommunityLayoutsForDimension.map(layout => ({
            value: communityLabelFor(layout.id),
            label: `${layout.title}*`,
            isCommunity: true,
            voteCount: layout.vote_count
        }))

        return [...builtIns, ...community]
    }, [builtInLayoutSet, promotedCommunityLayoutsForDimension])
    const approvedCommunityDesignOptions = useMemo(() => {
        return approvedCommunityLayoutsForDimension.map(layout => ({
            value: layout.id,
            label: layout.title,
            voteCount: layout.vote_count
        }))
    }, [approvedCommunityLayoutsForDimension])
    const currentCommunityLayout = useMemo(() => {
        return selectedApprovedCommunityDesignId
            ? (approvedCommunityLayoutsById[selectedApprovedCommunityDesignId] || null)
            : null
    }, [approvedCommunityLayoutsById, selectedApprovedCommunityDesignId])
    const hasCurrentUserVoted = useMemo(() => {
        if (!currentCommunityLayout) {
            return false
        }

        return Boolean(communityVoteBySubmissionId[currentCommunityLayout.id])
    }, [communityVoteBySubmissionId, currentCommunityLayout])
    const pendingCommunityLayoutOptions = useMemo(() => {
        return pendingCommunityLayouts.map(layout => ({
            value: layout.id,
            label: layout.title,
            previewImageUrl: layout.preview_image_url
        }))
    }, [pendingCommunityLayouts])
    const pendingCommunityLayoutsById = useMemo(() => {
        return pendingCommunityLayouts.reduce((acc, layout) => {
            acc[layout.id] = layout
            return acc
        }, {})
    }, [pendingCommunityLayouts])

    const updateConfig = useCallback(newConfig => {
        setConfig(currentConfig => ({...currentConfig, ...newConfig}))
    }, [])

    const dismissInstructions = useCallback(() => {
        trackEvent('dismiss_instructions', {
            source: 'overlay_button'
        })
        updateConfig({showInstructions: false})
    }, [updateConfig])

    const refreshCommunityData = useCallback(() => {
        setCommunityReloadKey(currentKey => currentKey + 1)
    }, [])

    const controlsConfig = useMemo(() => ({
        layoutOptions,
        colorStops,
        dimensions,
        squareSize,
        interval,
        bpm: config.bpm,
        note: config.note,
        updateMode,
        showInstructions,
        isUpdating,
        lockMode,
        isLayoutInverted,
        stagger,
        staggerMode,
        isGradientInverted,
        layoutInputValue: serializedLayoutValues,
        currentLayoutLabel: config.currentLayoutLabel,
        isCommunityConfigured,
        isCommunityLoading,
        isCommunitySubmitting,
        isCommunityVoting,
        isCommunityModerating,
        communityError,
        communityNotice,
        isCurrentLayoutCommunity: Boolean(currentCommunityLayout),
        currentCommunityVoteCount: currentCommunityLayout?.vote_count ?? 0,
        hasCurrentUserVoted,
        approvedCommunityDesignOptions,
        selectedApprovedCommunityDesignId,
        isCurrentUserAdmin,
        pendingCommunityLayoutOptions,
        pendingCommunityLayoutsById,
        isPendingCommunityLoading
    }), [
        layoutOptions,
        colorStops,
        dimensions,
        squareSize,
        interval,
        config.bpm,
        config.note,
        updateMode,
        showInstructions,
        isUpdating,
        lockMode,
        isLayoutInverted,
        stagger,
        staggerMode,
        isGradientInverted,
        serializedLayoutValues,
        config.currentLayoutLabel,
        isCommunityConfigured,
        isCommunityLoading,
        isCommunitySubmitting,
        isCommunityVoting,
        isCommunityModerating,
        communityError,
        communityNotice,
        currentCommunityLayout,
        hasCurrentUserVoted,
        approvedCommunityDesignOptions,
        selectedApprovedCommunityDesignId,
        isCurrentUserAdmin,
        pendingCommunityLayoutOptions,
        pendingCommunityLayoutsById,
        isPendingCommunityLoading
    ])

    const persistedConfig = useMemo(() => toPersistedConfig({
        dimensions,
        squareSize,
        bpm: config.bpm,
        note: config.note,
        duration,
        stagger,
        isUpdating,
        showInstructions,
        staggerMode,
        isSkewed,
        lockMode,
        updateMode
    }), [
        dimensions,
        squareSize,
        config.bpm,
        config.note,
        duration,
        stagger,
        isUpdating,
        showInstructions,
        staggerMode,
        isSkewed,
        lockMode,
        updateMode
    ])

    const applyManualValues = useCallback(nextValues => {
        setSelectedApprovedCommunityDesignId('')
        updateConfig({
            values: nextValues,
            currentLayoutLabel: null
        })
    }, [updateConfig])

    const copyValuesToClipboard = useCallback(async () => {
        const serializedValues = serializedLayoutValues.trim()

        if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(serializedValues)
            return
        }

        const fallbackNode = document.createElement('textarea')
        fallbackNode.value = serializedValues
        document.body.appendChild(fallbackNode)
        fallbackNode.select()
        document.execCommand('copy')
        document.body.removeChild(fallbackNode)
    }, [serializedLayoutValues])

    const renderLayoutCanvas = useCallback(async ({maxSide} = {}) => {
        const svgNode = kaleidoscopeRef.current
        if (!svgNode) {
            return null
        }

        const serializer = new XMLSerializer()
        let svgMarkup = serializer.serializeToString(svgNode)

        if (!svgMarkup.includes('xmlns=')) {
            svgMarkup = svgMarkup.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"')
        }
        if (!svgMarkup.includes('xmlns:xlink=')) {
            svgMarkup = svgMarkup.replace('<svg', '<svg xmlns:xlink="http://www.w3.org/1999/xlink"')
        }

        const svgBlob = new Blob([svgMarkup], {type: 'image/svg+xml;charset=utf-8'})
        const svgUrl = URL.createObjectURL(svgBlob)

        try {
            const image = await new Promise((resolve, reject) => {
                const img = new Image()
                img.onload = () => resolve(img)
                img.onerror = reject
                img.src = svgUrl
            })

            const viewBox = svgNode.viewBox?.baseVal
            const width = Math.max(1, Math.round(viewBox?.width || svgNode.clientWidth || 1))
            const height = Math.max(1, Math.round(viewBox?.height || svgNode.clientHeight || 1))

            const limit = Number.isFinite(maxSide) ? Math.max(1, Math.round(maxSide)) : Math.max(width, height)
            const scale = Math.min(1, limit / Math.max(width, height))
            const targetWidth = Math.max(1, Math.round(width * scale))
            const targetHeight = Math.max(1, Math.round(height * scale))

            const canvas = document.createElement('canvas')
            canvas.width = targetWidth
            canvas.height = targetHeight

            const context = canvas.getContext('2d')
            if (!context) {
                return null
            }

            context.fillStyle = '#000'
            context.fillRect(0, 0, targetWidth, targetHeight)
            context.drawImage(image, 0, 0, targetWidth, targetHeight)
            return canvas
        } finally {
            URL.revokeObjectURL(svgUrl)
        }
    }, [])

    const buildLayoutPreviewAsset = useCallback(async () => {
        const canvas = await renderLayoutCanvas({maxSide: previewMaxSide})
        if (!canvas) {
            return null
        }

        const webpBlob = await canvasToBlob(canvas, 'image/webp', previewWebpQuality)
        if (webpBlob && webpBlob.type === 'image/webp') {
            return {
                blob: webpBlob,
                contentType: 'image/webp',
                fileExtension: 'webp'
            }
        }

        const pngBlob = await canvasToBlob(canvas, 'image/png')
        if (pngBlob) {
            return {
                blob: pngBlob,
                contentType: 'image/png',
                fileExtension: 'png'
            }
        }

        const dataUrl = canvas.toDataURL('image/png')
        const fallbackResponse = await fetch(dataUrl)
        const fallbackBlob = await fallbackResponse.blob()
        return {
            blob: fallbackBlob,
            contentType: 'image/png',
            fileExtension: 'png'
        }
    }, [renderLayoutCanvas])

    const exportPng = useCallback(async () => {
        const canvas = await renderLayoutCanvas()
        if (!canvas) {
            return
        }

        const blob = await canvasToBlob(canvas, 'image/png')
        if (!blob) {
            return
        }

        const link = document.createElement('a')
        const pngUrl = URL.createObjectURL(blob)
        link.href = pngUrl
        link.download = `kaleidoscope-${Date.now()}.png`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(pngUrl)
    }, [renderLayoutCanvas])

    useEffect(() => {
        if (configPersistTimeoutRef.current) {
            clearTimeout(configPersistTimeoutRef.current)
        }

        configPersistTimeoutRef.current = setTimeout(() => {
            const serialized = encodeURIComponent(JSON.stringify(persistedConfig))
            document.cookie = `${CONFIG_COOKIE_NAME}=${serialized}; Max-Age=${CONFIG_COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax`
        }, CONFIG_COOKIE_DEBOUNCE_MS)

        return () => {
            if (configPersistTimeoutRef.current) {
                clearTimeout(configPersistTimeoutRef.current)
            }
        }
    }, [persistedConfig])

    useEffect(() => {
        updateConfig({
            values: buildQuadrant(dimensions),
            currentLayoutLabel: null
        })
    }, [dimensions, updateConfig])

    useEffect(() => {
        if (!isCommunityConfigured) {
            return
        }

        let isCanceled = false

        const bootstrap = async () => {
            try {
                await ensureAnonymousSession()
                if (!isCanceled) {
                    setIsCommunityAuthReady(true)
                }
            } catch (error) {
                if (!isCanceled) {
                    setCommunityError(error.message || 'Community auth is unavailable.')
                }
            }
        }

        bootstrap()

        return () => {
            isCanceled = true
        }
    }, [isCommunityConfigured])

    useEffect(() => {
        if (!isCommunityConfigured || !isCommunityAuthReady) {
            return
        }

        let isCanceled = false

        const loadAdminState = async () => {
            try {
                const isAdmin = await fetchIsCurrentUserAdmin()
                if (!isCanceled) {
                    setIsCurrentUserAdmin(isAdmin)
                }
            } catch (error) {
                if (!isCanceled) {
                    setIsCurrentUserAdmin(false)
                    setCommunityError(error.message || 'Unable to verify admin permissions.')
                }
            }
        }

        loadAdminState()

        return () => {
            isCanceled = true
        }
    }, [isCommunityAuthReady, isCommunityConfigured])

    useEffect(() => {
        if (!isCommunityConfigured || !isCommunityAuthReady) {
            return
        }

        let isCanceled = false

        const loadCommunityLayouts = async () => {
            setIsCommunityLoading(true)
            setCommunityError(null)

            try {
                const [promotedSubmissions, approvedSubmissions] = await Promise.all([
                    fetchPromotedCommunityLayouts({dimensions}),
                    fetchApprovedCommunityLayouts({dimensions})
                ])

                if (isCanceled) {
                    return
                }

                setPromotedCommunityLayouts(promotedSubmissions)
                setApprovedCommunityLayouts(approvedSubmissions)

                const submissionIds = approvedSubmissions.map(submission => submission.id)
                const votes = await fetchCurrentUserVotes(submissionIds)
                if (isCanceled) {
                    return
                }

                const votedMap = votes.reduce((acc, row) => {
                    acc[row.submission_id] = true
                    return acc
                }, {})
                setCommunityVoteBySubmissionId(votedMap)
            } catch (error) {
                if (!isCanceled) {
                    setPromotedCommunityLayouts([])
                    setApprovedCommunityLayouts([])
                    setCommunityVoteBySubmissionId({})
                    setCommunityError(error.message || 'Unable to load community designs.')
                }
            } finally {
                if (!isCanceled) {
                    setIsCommunityLoading(false)
                }
            }
        }

        loadCommunityLayouts()

        return () => {
            isCanceled = true
        }
    }, [communityReloadKey, dimensions, isCommunityAuthReady, isCommunityConfigured])

    useEffect(() => {
        if (!selectedApprovedCommunityDesignId) {
            return
        }

        if (approvedCommunityLayoutsById[selectedApprovedCommunityDesignId]) {
            return
        }

        setSelectedApprovedCommunityDesignId('')
    }, [approvedCommunityLayoutsById, selectedApprovedCommunityDesignId])

    useEffect(() => {
        if (!isCommunityConfigured || !isCommunityAuthReady || !isCurrentUserAdmin) {
            setPendingCommunityLayouts([])
            setIsPendingCommunityLoading(false)
            return
        }

        let isCanceled = false

        const loadPendingCommunityLayouts = async () => {
            setIsPendingCommunityLoading(true)
            setCommunityError(null)

            try {
                const pendingLayouts = await fetchPendingLayouts({dimensions})
                if (!isCanceled) {
                    setPendingCommunityLayouts(pendingLayouts)
                }
            } catch (error) {
                if (!isCanceled) {
                    setPendingCommunityLayouts([])
                    setCommunityError(error.message || 'Unable to load pending designs.')
                }
            } finally {
                if (!isCanceled) {
                    setIsPendingCommunityLoading(false)
                }
            }
        }

        loadPendingCommunityLayouts()

        return () => {
            isCanceled = true
        }
    }, [communityReloadKey, dimensions, isCommunityAuthReady, isCommunityConfigured, isCurrentUserAdmin])

    const getRandomColorStops = useCallback(currentStops => {
        const pool = gradients.length > 1 ? gradients.slice(1) : gradients
        const isSameStops = (left, right) => (
            Array.isArray(left) &&
            Array.isArray(right) &&
            left.length === right.length &&
            left.every((stop, index) => stop === right[index])
        )
        const available = pool.filter(gradient => (
            !isSameStops(gradient, currentStops) &&
            !isSameStops([...gradient].reverse(), currentStops)
        ))
        const candidates = available.length ? available : pool
        const randomGradient = candidates[Math.floor(Math.random() * candidates.length)]
        return {
            colorStops: [...randomGradient],
            isGradientInverted: coinFlip()
        }
    }, [])

    const handleSquareClick = useCallback(position => {
        setSelectedApprovedCommunityDesignId('')
        setConfig(currentConfig => {
            const updated = [...currentConfig.values]
            updated[position] = increment(currentConfig.values[position])

            return {
                ...currentConfig,
                currentLayoutLabel: null,
                values: updated
            }
        })
    }, [])

    const applyRandomLayoutInversion = useCallback(nextConfig => {
        if (nextConfig.lockMode === 'layout') {
            return nextConfig
        }

        return {
            ...nextConfig,
            isLayoutInverted: coinFlip()
        }
    }, [])

    const updateSingle = useCallback(() => {
        setSelectedApprovedCommunityDesignId('')
        setConfig(currentConfig => {
            const nextConfig = advanceSingle({
                config: currentConfig,
                dimensions,
                getRandomColorStops
            })

            return {
                ...nextConfig,
                currentLayoutLabel: null
            }
        })
    }, [dimensions, getRandomColorStops])

    const updateAll = useCallback(() => {
        setSelectedApprovedCommunityDesignId('')
        setConfig(currentConfig => {
            const nextConfig = advanceAll({
                config: currentConfig,
                dimensions,
                getRandomColorStops
            })

            const withRandomInversion = applyRandomLayoutInversion(nextConfig)
            if (withRandomInversion.values === currentConfig.values) {
                return withRandomInversion
            }

            return {
                ...withRandomInversion,
                currentLayoutLabel: null
            }
        })
    }, [applyRandomLayoutInversion, dimensions, getRandomColorStops])

    const updateLayout = useCallback(() => {
        setSelectedApprovedCommunityDesignId('')
        setConfig(currentConfig => {
            const nextConfig = advanceLayout({
                config: currentConfig,
                dimensions,
                getRandomColorStops,
                layouts: mergedLayouts
            })

            return applyRandomLayoutInversion(nextConfig)
        })
    }, [applyRandomLayoutInversion, dimensions, getRandomColorStops, mergedLayouts])

    const handleLayoutSelect = useCallback(label => {
        setSelectedApprovedCommunityDesignId('')
        if (!label) {
            setConfig(currentConfig => ({
                ...currentConfig,
                currentLayoutLabel: null
            }))
            return
        }

        setConfig(currentConfig => {
            return selectLayoutByLabel({
                config: currentConfig,
                label,
                layouts: mergedLayouts
            })
        })
    }, [mergedLayouts])
    const handleSelectApprovedCommunityDesign = useCallback(submissionId => {
        const normalizedSubmissionId = submissionId || ''
        setSelectedApprovedCommunityDesignId(normalizedSubmissionId)

        if (!normalizedSubmissionId) {
            return
        }

        const submission = approvedCommunityLayoutsById[normalizedSubmissionId]
        if (!submission) {
            return
        }

        updateConfig({
            currentLayoutLabel: null,
            values: submission.layout_values
        })
        setCommunityError(null)
        setCommunityNotice(null)
    }, [approvedCommunityLayoutsById, updateConfig])

    const handleGradientSelect = useCallback(index => {
        const gradient = gradients[index]
        if (!gradient) {
            return
        }

        updateConfig({colorStops: [...gradient]})
    }, [updateConfig])

    const handleSubmitLayout = useCallback(async ({title}) => {
        if (!isCommunityConfigured) {
            setCommunityError('Supabase is not configured.')
            return {ok: false}
        }
        if (!submissionTitlePattern.test(title) || title.length > 20) {
            const message = 'Title must be lowercase letters, numbers, and dashes only (max 20 chars).'
            setCommunityError(message)
            return {
                ok: false,
                error: message
            }
        }

        setIsCommunitySubmitting(true)
        setCommunityError(null)
        setCommunityNotice(null)

        try {
            await ensureAnonymousSession()
            const previewAsset = await buildLayoutPreviewAsset()
            if (!previewAsset?.blob) {
                throw new Error('Unable to capture preview image for this design.')
            }

            const userId = await getAuthenticatedUserId()
            if (!userId) {
                throw new Error('Unable to identify the current user for preview upload.')
            }

            const uploadedPreview = await uploadLayoutPreview({
                previewBlob: previewAsset.blob,
                contentType: previewAsset.contentType,
                fileExtension: previewAsset.fileExtension,
                userId
            })

            await submitLayout({
                title,
                previewImageUrl: uploadedPreview.publicUrl,
                dimensions,
                layoutValues: values
            })
            setCommunityNotice('Design submitted for review.')
            refreshCommunityData()
            return {ok: true}
        } catch (error) {
            const message = toSubmitDesignErrorMessage(error)
            setCommunityError(message)
            return {
                ok: false,
                error: message
            }
        } finally {
            setIsCommunitySubmitting(false)
        }
    }, [buildLayoutPreviewAsset, dimensions, isCommunityConfigured, refreshCommunityData, values])

    const handleToggleCurrentVote = useCallback(async () => {
        if (!currentCommunityLayout) {
            return
        }

        setIsCommunityVoting(true)
        setCommunityError(null)
        setCommunityNotice(null)

        try {
            await ensureAnonymousSession()
            const voteState = await toggleLayoutVote(currentCommunityLayout.id)
            const voted = Boolean(voteState.voted)
            const voteCount = Number(voteState.vote_count || 0)

            setCommunityVoteBySubmissionId(currentVotes => ({
                ...currentVotes,
                [currentCommunityLayout.id]: voted
            }))
            setApprovedCommunityLayouts(currentLayouts => currentLayouts.map(layout => {
                if (layout.id !== currentCommunityLayout.id) {
                    return layout
                }

                return {
                    ...layout,
                    vote_count: voteCount
                }
            }))
            setCommunityNotice(voted ? 'Vote added.' : 'Vote removed.')
        } catch (error) {
            setCommunityError(error.message || 'Unable to update vote.')
        } finally {
            setIsCommunityVoting(false)
        }
    }, [currentCommunityLayout])

    const handlePreviewPendingLayout = useCallback(submissionId => {
        const submission = pendingCommunityLayoutsById[submissionId]
        if (!submission) {
            return
        }

        setSelectedApprovedCommunityDesignId('')
        updateConfig({
            currentLayoutLabel: null,
            values: submission.layout_values
        })
        setCommunityNotice(`Previewing pending design: ${submission.title}`)
        setCommunityError(null)
    }, [pendingCommunityLayoutsById, updateConfig])

    const handleModeratePendingLayout = useCallback(async ({submissionId, nextStatus}) => {
        if (!isCurrentUserAdmin || !submissionId) {
            return {ok: false}
        }

        setIsCommunityModerating(true)
        setCommunityNotice(null)
        setCommunityError(null)

        try {
            await ensureAnonymousSession()
            await moderateLayoutSubmission({submissionId, nextStatus})

            setCommunityNotice(nextStatus === 'promoted' ? 'Submission promoted.' : 'Submission approved.')
            refreshCommunityData()
            return {ok: true}
        } catch (error) {
            const message = error.message || 'Unable to moderate submission.'
            setCommunityError(message)
            return {
                ok: false,
                error: message
            }
        } finally {
            setIsCommunityModerating(false)
        }
    }, [isCurrentUserAdmin, refreshCommunityData])
    const handleDeletePendingLayout = useCallback(async submissionId => {
        if (!isCurrentUserAdmin || !submissionId) {
            return {ok: false}
        }

        setIsCommunityModerating(true)
        setCommunityNotice(null)
        setCommunityError(null)

        try {
            await ensureAnonymousSession()
            await deletePendingLayoutSubmission(submissionId)

            setCommunityNotice('Pending submission deleted.')
            refreshCommunityData()
            return {ok: true}
        } catch (error) {
            const message = error.message || 'Unable to delete pending submission.'
            setCommunityError(message)
            return {
                ok: false,
                error: message
            }
        } finally {
            setIsCommunityModerating(false)
        }
    }, [isCurrentUserAdmin, refreshCommunityData])

    const updateSequence = useCallback(() => {
        setSelectedApprovedCommunityDesignId('')
        setConfig(currentConfig => {
            const nextConfig = advanceSequence({
                config: currentConfig,
                dimensions,
                getRandomColorStops,
                sequence
            })

            const withRandomInversion = applyRandomLayoutInversion(nextConfig)
            if (withRandomInversion.values === currentConfig.values) {
                return withRandomInversion
            }

            return {
                ...withRandomInversion,
                currentLayoutLabel: null
            }
        })
    }, [applyRandomLayoutInversion, dimensions, getRandomColorStops])

    const modeUpdater = useMemo(() => ({
        single: updateSingle,
        all: updateAll,
        layout: updateLayout,
        sequence: updateSequence
    }), [updateAll, updateLayout, updateSequence, updateSingle])

    useEffect(() => {
        if (!isUpdating) {
            return undefined
        }

        const tick = modeUpdater[updateMode]
        if (!tick) {
            return undefined
        }

        const timer = setInterval(tick, interval)

        return () => clearInterval(timer)
    }, [interval, isUpdating, modeUpdater, updateMode])

    return (
        <Container>
            <Skewer isUpdating={isUpdating && isSkewed}>
                <Frame $size={(dimensions * 2) * squareSize} $isDark={true}>
                    <Kaleidoscope
                        ref={kaleidoscopeRef}
                        dimensions={dimensions}
                        squareSize={squareSize}
                        values={effectiveValues}
                        updateValue={handleSquareClick}
                        isUpdating={isUpdating}
                        colors={colors}
                        duration={duration}
                        stagger={stagger}
                        isStaggered={isStaggered}
                        isReversed={isReversed}
                    />
                </Frame>
            </Skewer>
            <Controls
                config={controlsConfig}
                setConfig={setConfig}
                setValues={applyManualValues}
                onCopy={copyValuesToClipboard}
                onExportPng={exportPng}
                onSelectLayoutLabel={handleLayoutSelect}
                onSelectApprovedCommunityDesign={handleSelectApprovedCommunityDesign}
                onSelectGradient={handleGradientSelect}
                onSubmitLayout={handleSubmitLayout}
                onToggleCurrentVote={handleToggleCurrentVote}
                onPreviewPendingLayout={handlePreviewPendingLayout}
                onModeratePendingLayout={handleModeratePendingLayout}
                onDeletePendingLayout={handleDeletePendingLayout}
            />
            {showInstructions && (
                <InstructionOverlay>
                    <InstructionPanel>
                        <InstructionBody>
                            <div style={{fontWeight: 700, marginBottom: 10}}>Quick Start</div>
                            <ol style={{margin: '0 0 12px 18px', padding: 0, lineHeight: 1.45}}>
                                <li>Hover or tap the top-left corner to open controls.</li>
                                <li>Set BPM to sync visual updates with music.</li>
                                <li>Turn off auto-update to click squares and edit the design manually.</li>
                                <li>When ready, open Community and submit your current design.</li>
                                <li>Approved community designs can be upvoted and promoted into rotation.</li>
                            </ol>
                        </InstructionBody>
                        <InstructionButton onClick={dismissInstructions}>
                            Hide Instructions
                        </InstructionButton>
                    </InstructionPanel>
                </InstructionOverlay>
            )}
        </Container>
    )
}

export default App
